import { GetServerSidePropsContext } from 'next'

import {
  subcatsOutcomes as SubcatsOutcomesFixture,
  outcomesContent as OutcomesContentFixture,
} from '@mc/growth-data'

import { retrieveWithCache } from '@mc/next-common'
import {
  CourseMeta,
  CourseOutcome,
  Outcome,
  OutcomeContentRaw,
  Session,
} from 'types/data'
import { getArticlesBySlugs } from './articles'
import { getAPIAllCoursesDeep, getCourse } from './courses'
import { getSessions } from './sessions'
import { getGrowthSubcategoryBySlug } from './growth'
import {
  getContentListFromCourses,
  getContentListFromSessions,
} from '../../utils'
import { transformCourseToCourseMeta } from '../../utils/outcomes/transform'

type Values<T> = T[keyof T]

type SubcatsOutcomes = { [k: string]: Values<typeof SubcatsOutcomesFixture> }
type OutcomesContent = { [k: string]: Values<typeof OutcomesContentFixture> }

const subcategoryToOutcomesImport = () =>
  import(
    /* webpackChunkName: "subcats-outcomes" */
    '@mc/growth-data/outcomes/subcats-outcomes.json'
  ).then((m) => m.default as SubcatsOutcomes)

const outcomesToContentsImport = () =>
  import(
    /* webpackChunkName: "outcomes" */
    '@mc/growth-data/outcomes/outcomes.json'
  ).then((m) => m.default as OutcomesContent)

const courseOutcomesImport = () =>
  import(
    /* webpackChunkName: "course-outcomes" */
    '@mc/growth-data/outcomes/course-outcomes.json'
  ).then((m) => m.default)

const getAllSubcategoryToOutcomesFixture = async () =>
  retrieveWithCache('subcat-outcomes-fixture', () =>
    subcategoryToOutcomesImport(),
  )

const getOutcomesToContentsFixture = async () =>
  retrieveWithCache('outcome-contents-fixture', () =>
    outcomesToContentsImport(),
  ) as Promise<Record<string, OutcomeContentRaw>>

const getCourseOutcomesFixture = async () =>
  retrieveWithCache('course-outcome-fixture', () =>
    courseOutcomesImport(),
  ) as Promise<Record<string, CourseOutcome[]>>

const getSubcategoryToOutcomesFromSlug = async (subcatSlug: string) => {
  const allSubcatToOutcomes = await getAllSubcategoryToOutcomesFixture()

  return allSubcatToOutcomes[subcatSlug as keyof typeof allSubcatToOutcomes]
}

const getSubcatOutcomeSlugs = async (subcatSlug: string) => {
  const subcatToOutcomes = await getSubcategoryToOutcomesFromSlug(subcatSlug)
  return subcatToOutcomes?.outcomeSlugs || []
}

const getOutcomesToContentBySubcat = async (subcatSlug: string) => {
  const [outcomeSlugs, allOutcomes] = await Promise.all([
    getSubcatOutcomeSlugs(subcatSlug),
    getOutcomesToContentsFixture(),
  ])

  return outcomeSlugs.reduce(
    (obj, outcomeSlug) => {
      if (allOutcomes[outcomeSlug]) {
        obj[outcomeSlug] = allOutcomes[outcomeSlug]
      }
      return obj
    },
    {} as Record<string, OutcomeContentRaw>,
  )
}

const getSortedSubcategoriesForOutcome = async (outcomeSlug: string) => {
  const allSubcatToOutcomes = await getAllSubcategoryToOutcomesFixture()

  return Object.entries(allSubcatToOutcomes)
    .reduce(
      (acc, [subcategory, details]) => {
        const index = details.outcomeSlugs.indexOf(outcomeSlug)
        if (index !== -1) {
          const score = details.outcomeScores[index]
          acc.push({ subcategory, score })
        }
        return acc
      },
      [] as { subcategory: string; score: number }[],
    )
    .sort((a, b) => b.score - a.score)
    .map((item) => item.subcategory)
}

const getAllCoursesAndSessions = async (ctx: GetServerSidePropsContext) => {
  const [allCoursesComplete, allSessionsComplete] = await Promise.all([
    getAPIAllCoursesDeep(ctx),
    getSessions(ctx) as Promise<Session[]>,
  ])
  return { allCoursesComplete, allSessionsComplete }
}

export const getPrimarySubcategoryForOutcome = async (outcomeSlug: string) => {
  const subcategories = await getSortedSubcategoriesForOutcome(outcomeSlug)
  return subcategories[0]
}

const getOutcomeToContentRaw = async (outcomeSlug: string) => {
  const allOutcomes = await getOutcomesToContentsFixture()
  return allOutcomes[outcomeSlug]
}

export const getOutcomeToContentsMapBySubcat = async (
  ctx: GetServerSidePropsContext,
  subcatSlug: string,
) => {
  const [
    subcatOutcomesWithContent,
    { allCoursesComplete, allSessionsComplete },
  ] = await Promise.all([
    getOutcomesToContentBySubcat(subcatSlug),
    getAllCoursesAndSessions(ctx),
  ])

  const courseSlugToMeta: Record<string, CourseMeta> = {}
  const outcomesBySlug: Record<string, Outcome> = {}

  Object.entries(subcatOutcomesWithContent).forEach(
    ([outcomeSlug, outcomeContentRaw]) => {
      const contentListFromCourses = getContentListFromCourses(
        allCoursesComplete,
        courseSlugToMeta,
        outcomeContentRaw,
        outcomeSlug,
      )

      const contentListFromSessions = getContentListFromSessions(
        allSessionsComplete,
        courseSlugToMeta,
        outcomeContentRaw,
        outcomeSlug,
      )

      outcomesBySlug[outcomeSlug] = {
        id: outcomeContentRaw.id,
        slug: outcomeSlug,
        name: outcomeContentRaw.name,
        oneLiner: outcomeContentRaw.oneLiner,
        contents: [...contentListFromCourses, ...contentListFromSessions],
      }
    },
  )

  return { outcomesBySlug, courseSlugToMeta }
}

export const getInstructorsForSubcatSlug = async (
  ctx: GetServerSidePropsContext,
  subcatSlug: string,
) => {
  const { courseSlugToMeta } = await getOutcomeToContentsMapBySubcat(
    ctx,
    subcatSlug,
  )
  return Object.values(courseSlugToMeta).filter(
    (c) => c.outcomeSlugs.length > 0,
  )
}

export const outcomeExists = async (outcomeSlug: string) => {
  const allOutcomes = await getOutcomesToContentsFixture()
  return !!allOutcomes[outcomeSlug]
}

export const getOutcomeContent = async (
  ctx: GetServerSidePropsContext,
  outcomeSlug: string,
) => {
  const key = `getOutcomeContent-${outcomeSlug}`
  return retrieveWithCache(key, () =>
    getOutcomeContentUncached(ctx, outcomeSlug),
  )
}

const getOutcomeContentUncached = async (
  ctx: GetServerSidePropsContext,
  outcomeSlug: string,
) => {
  const [outcomeToContentRaw, { allCoursesComplete, allSessionsComplete }] =
    await Promise.all([
      getOutcomeToContentRaw(outcomeSlug),
      getAllCoursesAndSessions(ctx),
    ])

  const courseSlugToMeta: Record<string, CourseMeta> = {}

  const contentListFromCourses = getContentListFromCourses(
    allCoursesComplete,
    courseSlugToMeta,
    outcomeToContentRaw,
    outcomeSlug,
  )

  const contentListFromSessions = getContentListFromSessions(
    allSessionsComplete,
    courseSlugToMeta,
    outcomeToContentRaw,
    outcomeSlug,
  )

  const outcome: Outcome = {
    id: outcomeToContentRaw.id,
    slug: outcomeSlug,
    name: outcomeToContentRaw.name,
    oneLiner: outcomeToContentRaw.oneLiner,
    contents: [...contentListFromCourses, ...contentListFromSessions],
  }

  allCoursesComplete.forEach((course) => {
    if (!(course.slug in courseSlugToMeta)) {
      courseSlugToMeta[course.slug] = transformCourseToCourseMeta(course, [])
    }
  })

  return { outcome, courseSlugToMeta }
}

export const getSubcategoryToOutcomesMap = async (
  ctx: GetServerSidePropsContext,
  subcatSlug: string,
) => {
  const subcatToOutcomes = await getSubcategoryToOutcomesFromSlug(subcatSlug)

  if (!subcatToOutcomes) return {}

  const subcatArticleSlugs = subcatToOutcomes.articleSlugs

  const [enrichedArticles, subcategory] = await Promise.all([
    getArticlesBySlugs(ctx, subcatArticleSlugs),
    getGrowthSubcategoryBySlug(ctx, subcatSlug),
  ])

  return {
    ...subcatToOutcomes,
    name: subcategory?.name || subcatToOutcomes.name,
    articles: enrichedArticles.filter((a) => a !== null),
    id: subcategory?.id || 0,
  }
}

export const getOutcomesForCourses = () => getCourseOutcomesFixture()

// start pen-3644_v3 experiment
export type ClassesOutcomesKeyLearnings = {
  [key: string]: {
    outcomes: {
      title: string
      slug?: string
      keyLearnings: string[]
    }[]
  }
}

export type OutcomeKeyLearnings = {
  title: string
  slug: string
  keyLearnings: string[]
  lessonVideoThumbUrl: string
}

const classesOutcomesKeyLearningsImport = () =>
  import(
    /* webpackChunkName: "classes-outcomes-key-learnings" */
    '@mc/growth-data/CMOutcomes/classes-outcomes-key-learnings.json'
  ).then((m) => m.default as ClassesOutcomesKeyLearnings)

const topClassesImport = () =>
  import(
    /* webpackChunkName: "top-classes" */
    '@mc/growth-data/CMOutcomes/top-classes.json'
  ).then((m) => m.default as string[])

const getTopClassesFixture = async () =>
  retrieveWithCache('top-classes', () => topClassesImport())

const getClassesOutcomesKeyLearningsFixture = async () =>
  retrieveWithCache('classes-outcomes-key-learnings', () =>
    classesOutcomesKeyLearningsImport(),
  )

export const getOutcomesKeyLearnings = async (
  course: Awaited<ReturnType<typeof getCourse>>,
) => {
  // show experiment just for the top classes/series
  const topClasses = await getTopClassesFixture()
  const slug = course.slug || ''
  if (!topClasses.includes(slug)) return null

  const classesOutcomesKeyLearnings =
    await getClassesOutcomesKeyLearningsFixture()

  const outcomes = classesOutcomesKeyLearnings[slug]?.outcomes
  if (!outcomes) return null

  // get lesson video thumb url for each outcome
  // Create a copy of sortedChapters so that we avoid returning repeated videoThumbUrl images for different outcomes
  let remainingChapters = [...(course.sortedChapters || [])]
  const data = await Promise.all(
    outcomes.map(async ({ slug: outcomeSlug, title, keyLearnings }) => {
      const outcomeContent = await getOutcomeToContentRaw(outcomeSlug || '')
      const chapterIds = outcomeContent?.chapterIds
      // in the cases where we hardcoded outcomes, we don't have chapterIds
      // so we need to get the first chapter and use that as the lesson video thumb url

      let chapter = null
      if (remainingChapters.length > 0) {
        chapter =
          remainingChapters.find(({ id }) => chapterIds?.includes(id)) ??
          remainingChapters[0]
      } else {
        chapter =
          course.sortedChapters?.find(({ id }) => chapterIds?.includes(id)) ??
          course.sortedChapters?.[0]
      }

      if (!chapter) return null

      remainingChapters = remainingChapters.filter(
        ({ id }) => id !== chapter.id,
      )

      return {
        slug: outcomeSlug,
        title,
        keyLearnings,
        lessonVideoThumbUrl: chapter.videoThumbUrl,
      }
    }),
  )

  return data
}
// end pen-3644_v3 experiment
